import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Shield, ArrowRight, Clock } from 'lucide-react';

const ResiliencePopup = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if user already dismissed the popup this session
    const dismissed = sessionStorage.getItem('resilience-popup-dismissed');
    if (dismissed) return;

    // Show popup after 8 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('resilience-popup-dismissed', 'true');
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="resilience-popup"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 animate-fade-in"
      style={{ animation: 'fadeIn 0.3s ease-out' }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-basalt-carbon/70 backdrop-blur-sm"
        onClick={handleClose}
        data-testid="popup-backdrop"
      />

      {/* Popup Card */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        style={{ animation: 'slideUp 0.4s ease-out' }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          data-testid="popup-close"
          className="absolute top-4 right-4 z-10 text-basalt-carbon/60 hover:text-basalt-carbon bg-white/80 rounded-full p-2 hover:bg-white transition-all"
          aria-label="Close popup"
        >
          <X size={20} />
        </button>

        {/* Header with brand gradient */}
        <div
          className="relative px-8 pt-10 pb-8 text-ozone-white text-center overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #0F2E1F 0%, #2D5A27 100%)',
          }}
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-solar-kinetic/20 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-solar-kinetic/15 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl"></div>

          <div className="relative">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-solar-kinetic rounded-full mb-4 shadow-lg">
              <Shield size={32} className="text-basalt-carbon" />
            </div>
            <div className="inline-flex items-center space-x-2 bg-solar-kinetic/20 text-solar-kinetic px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
              <Clock size={12} />
              <span>Free · 3 Minutes</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-3 leading-tight">
              How resilient is <span className="text-solar-kinetic">your organisation?</span>
            </h3>
            <p className="text-base text-ozone-white/90 leading-relaxed">
              Uncover your vulnerability to uncertainty, climate change, and disruption — with instant results.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="p-8">
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-kabi-teal/5 border border-kabi-teal/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-kabi-teal">10</div>
              <div className="text-xs text-basalt-carbon/70 font-medium">Quick Questions</div>
            </div>
            <div className="bg-kabi-teal/5 border border-kabi-teal/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-kabi-teal">5</div>
              <div className="text-xs text-basalt-carbon/70 font-medium">Domain Scores</div>
            </div>
          </div>

          <p className="text-sm text-basalt-carbon/70 text-center mb-6 leading-relaxed">
            Get a personalised resilience report + actionable next steps for your organisation.
          </p>

          <Link
            to="/quiz"
            onClick={handleClose}
            data-testid="popup-cta"
            className="w-full inline-flex items-center justify-center space-x-2 bg-solar-kinetic text-basalt-carbon px-6 py-4 rounded-full font-bold hover:bg-solar-kinetic/90 transition-all shadow-md"
          >
            <span>Take the Resilience Quiz</span>
            <ArrowRight size={20} />
          </Link>

          <button
            onClick={handleClose}
            data-testid="popup-maybe-later"
            className="w-full text-center text-sm text-basalt-carbon/50 hover:text-basalt-carbon mt-4 py-2 transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default ResiliencePopup;
